import { MouseEventHandler, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Modal from '@mui/material/Modal'
import styles from './styles.module.css'
import PrimaryButton from '../../../../../global/components/buttons/primary-button/PrimaryButton'
import SecondaryButton from '../../../../../global/components/buttons/secondary-button/SecondaryButton'
import TechnicianModalContent from './TechnicianModalContent'
import CircularProgress from '@mui/material/CircularProgress'
import {
  createTech,
  createTechnicianWeekCalendars,
  editTech,
  editTechnicianWeekCalendar,
  getPicturesByIds,
  getTechById,
  setTechToLocation,
} from '../../api'
import { fetchData } from '../../../../../global/utils/fetch'
import ToastNotification, {
  IAlertStatusProps,
} from '../../../../../global/components/toast-alert/ToastNotification'
import {
  ITechnician,
  ITechnicianPartial,
} from '../../../../../../../app/entities/Technician'
import {
  IWeekCalendar,
  IWeekCalendarPartial,
} from '../../../../../../../app/entities/WeekCalendar'
import {
  initialWeekCalendarFormValues,
  parseTimeStringToMinutes,
} from '../utils'
import { ILocation } from '../../../../../../../app/entities/Location'

interface ITechnicianModalProps {
  isOpen: boolean
  handleClosed: MouseEventHandler
  technicianObjectId?: string
}

export function TechnicianModal({
  isOpen,
  handleClosed,
  technicianObjectId,
}: ITechnicianModalProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [alertStatus, setAlertStatus] = useState<IAlertStatusProps>({
    isOpen: false,
    message: '',
    severity: 'error',
  })
  const [technicianData, setTechnicianData] = useState<ITechnicianPartial>({})
  const [weekCalendars, setWeekCalendars] = useState<IWeekCalendarPartial[]>(
    initialWeekCalendarFormValues,
  )
  const [location, setLocation] = useState<ILocation | null>(null)
  const [isDefaultLunch, setIsDefaultLunch] = useState(true)
  const [isDefaultWorkDay, setIsDefaultWorkDay] = useState(true)
  const [isSaveDisabled, setIsSaveDisabled] = useState(true)

  function handleClose(e: any) {
    setTechnicianData({})
    setWeekCalendars(initialWeekCalendarFormValues)
    setLocation(null)
    setIsLoading(false)
    handleClosed(e)
  }

  async function handleSaveTech(e: any) {
    try {
      setIsLoading(true)
      let dataToSave = { ...technicianData }
      if (isDefaultWorkDay && location) {
        dataToSave = {
          ...dataToSave,
          workDayFrom: location.workDayFrom,
          workDayTo: location.workDayTo,
        }
      }
      if (isDefaultLunch && location) {
        dataToSave = {
          ...dataToSave,
          lunchDuration: location.lunchDuration,
        }
      }
      await Promise.all([
        fetchData(editTech(dataToSave)),
        fetchData<IWeekCalendar>(
          editTechnicianWeekCalendar(weekCalendars[0], technicianObjectId),
        ),
        fetchData<IWeekCalendar>(
          editTechnicianWeekCalendar(weekCalendars[1], technicianObjectId),
        ),
      ])
      await fetchData(
        setTechToLocation(location?.objectId ?? '', technicianObjectId ?? ''),
      )
      handleClose(e)
    } catch (error) {
      alert(
        `Error updating Technician: ${JSON.stringify(
          error,
          Object.getOwnPropertyNames(error),
        )}`,
      )
    } finally {
      setIsLoading(false)
    }
  }

  async function handleCreateTech(e: any) {
    try {
      setIsLoading(true)
      let dataToSave = { ...technicianData }
      if (isDefaultWorkDay && location) {
        dataToSave = {
          ...dataToSave,
          workDayFrom: location.workDayFrom,
          workDayTo: location.workDayTo,
        }
      }
      if (isDefaultLunch && location) {
        dataToSave = {
          ...dataToSave,
          lunchDuration: location.lunchDuration,
        }
      }
      const newTechnician = await fetchData<ITechnician>(createTech(dataToSave))
      await Promise.all([
        fetchData<void>(
          createTechnicianWeekCalendars(weekCalendars, newTechnician.objectId),
        ),
        fetchData<void>(
          setTechToLocation(location?.objectId ?? '', newTechnician.objectId),
        ),
      ])
      handleClose(e)
    } catch (error) {
      alert(
        `Error updating Technician: ${JSON.stringify(
          error,
          Object.getOwnPropertyNames(error),
        )}`,
      )
    } finally {
      setIsLoading(false)
    }
  }

  async function fetchTechnicianData(technicianObjectId: string) {
    setIsLoading(true)
    const [newTechnician, technicianProfilePictures] = await Promise.all([
      fetchData<ITechnician>(getTechById(technicianObjectId)),
      fetchData<
        {
          technicianObjectId: string
          profilePictureURL?: string
          orderTrackerPictureURL?: string
        }[]
      >(getPicturesByIds([technicianObjectId])),
    ])
    setTechnicianData({
      ...newTechnician,
      profilePicture: technicianProfilePictures[0].profilePictureURL,
    })
    const isDefault =
      newTechnician.location &&
      newTechnician.workDayFrom === newTechnician.location.workDayFrom &&
      newTechnician.workDayTo === newTechnician.location.workDayTo
    setIsDefaultWorkDay(!!isDefault)
    const isDefaultLunchValue =
      newTechnician.location &&
      (newTechnician.lunchDuration === undefined ||
        newTechnician.lunchDuration === null ||
        newTechnician.lunchDuration === newTechnician.location.lunchDuration)
    setIsDefaultLunch(!!isDefaultLunchValue)
    setWeekCalendars(newTechnician.weekCalendars)
    setLocation(newTechnician.location ?? null)
    setIsLoading(false)
  }

  useEffect(() => {
    if (!technicianObjectId) return
    fetchTechnicianData(technicianObjectId)
  }, [technicianObjectId])

  useEffect(() => {
    const useDefaultWorkDay =
      !technicianData.workDayTo || !technicianData.workDayFrom
    const isValidTechnicianWorkTime = useDefaultWorkDay
      ? true
      : parseTimeStringToMinutes(technicianData.workDayTo) >
        parseTimeStringToMinutes(technicianData.workDayFrom)

    const isTechnicianFormInfoInvalid =
      !technicianData.name ||
      !technicianData.email ||
      !location?.objectId ||
      !technicianData.employeeId ||
      !technicianData.samsaraId ||
      !isValidTechnicianWorkTime

    setIsSaveDisabled(isTechnicianFormInfoInvalid)
  }, [technicianData, location])

  function toggleAlert(open: boolean) {
    setAlertStatus({ ...alertStatus, isOpen: open })
  }

  return (
    <div>
      <Modal
        open={isOpen}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box className={styles.techModal}>
          <>
            <div className={styles.locationModalHeader}>
              <ToastNotification
                severity={alertStatus.severity}
                open={alertStatus.isOpen}
                message={alertStatus.message}
                handleClose={() => toggleAlert(false)}
              />
              <div className={`${styles.locationModalPageTitle} font--bold`}>
                {technicianObjectId
                  ? 'Edit Technician'
                  : 'Create New Technician'}
              </div>
              <div className={styles.locationModalHeaderButtons}>
                <SecondaryButton
                  buttonName='Cancel'
                  onClick={handleClose}
                />
                {technicianObjectId ? (
                  <PrimaryButton
                    disabled={isLoading || isSaveDisabled}
                    buttonName='Save Changes'
                    onClick={handleSaveTech}
                  />
                ) : (
                  <PrimaryButton
                    buttonName='Create Tech'
                    disabled={isLoading || isSaveDisabled}
                    onClick={handleCreateTech}
                  />
                )}
              </div>
            </div>
            {isLoading && (
              <div className={styles.progressContainer}>
                <CircularProgress />
              </div>
            )}
            {!isLoading && (
              <TechnicianModalContent
                technicianData={technicianData}
                setTechnicianData={setTechnicianData}
                isDefaultLunch={isDefaultLunch}
                setIsDefaultLunch={setIsDefaultLunch}
                isDefaultWorkDay={isDefaultWorkDay}
                setIsDefaultWorkDay={setIsDefaultWorkDay}
                technicianObjectId={technicianObjectId}
                weekCalendars={weekCalendars}
                setWeekCalendars={setWeekCalendars}
                location={location}
                setLocation={setLocation}
                refetchTechnicianData={
                  technicianObjectId
                    ? () => fetchTechnicianData(technicianObjectId)
                    : () => {}
                }
              />
            )}
          </>
        </Box>
      </Modal>
    </div>
  )
}
